<template>
    <div>
        <notification-container :notifications="notifications"/>
        <div class="language-selector" ref="languageSelector">
            <div
                class="language-toggle"
                :class="{ 'open': isDropdownOpen }"
                @click="toggleDropdown"
                @mouseenter="handleMouseEnter"
                @mouseleave="handleMouseLeave"
                :title="currentLanguage ? currentLanguage.text : ''"
            >
                <span class="language-code">{{ currentLanguage ? currentLanguage.shortText : selectedLanguage.toUpperCase() }}</span>
            </div>

            <div
                class="language-dropdown"
                :class="{ 'open': isDropdownOpen }"
                @mouseenter="handleDropdownMouseEnter"
                @mouseleave="handleDropdownMouseLeave"
            >
                <button
                    v-for="language in otherLanguages"
                    :key="language.value"
                    class="language-option"
                    @click="selectLanguage(language.value)"
                    :disabled="isLoading"
                    :title="language.text"
                >
                    <span class="language-code">{{ language.shortText }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import NotificationContainer from "./NotificationContainer.vue";
import notificationsMixin from "../../mixins/notifications";
import {i18n, setLocale} from "../../utils/i18n";

export default {
    name: 'LanguageSwitcher',
    components: {
        NotificationContainer,
    },
    mixins: [notificationsMixin],
    props: {
        customLanguages: {
            type: Array,
            required: true
        },
        lang: {
            type: String,
            default: 'en_GB',
        },
    },
    created() {
        setLocale(this.lang);
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
        // Check if device supports hover interactions
        const supportsHover = window.matchMedia('(hover: hover)').matches;
        this.isTouchDevice = !supportsHover;
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
    },
    data() {
        return {
            selectedLanguage: this.lang,
            isLoading: false,
            isDropdownOpen: false,
            closeTimer: null,
            isTouchDevice: false,
        }
    },
    computed: {
        languages() {
            return this.customLanguages;
        },
        currentLanguage() {
            return this.languages.find(lang => lang.value === this.selectedLanguage);
        },
        otherLanguages() {
            return this.languages.filter(lang => lang.value !== this.selectedLanguage);
        },
    },
    methods: {
        toggleDropdown() {
            if (!this.isLoading) {
                this.isDropdownOpen = !this.isDropdownOpen;
            }
        },
        openDropdown() {
            if (!this.isLoading) {
                this.cancelCloseTimer();
                this.isDropdownOpen = true;
            }
        },
        closeDropdown() {
            this.isDropdownOpen = false;
        },
        startCloseTimer() {
            this.closeTimer = setTimeout(() => {
                this.closeDropdown();
            }, 300); // 300ms delay before closing
        },
        cancelCloseTimer() {
            if (this.closeTimer) {
                clearTimeout(this.closeTimer);
                this.closeTimer = null;
            }
        },
        handleMouseEnter() {
            // Only open on hover if not a touch device
            if (!this.isTouchDevice) {
                this.openDropdown();
            }
        },
        handleMouseLeave() {
            // Only start close timer on hover if not a touch device
            if (!this.isTouchDevice) {
                this.startCloseTimer();
            }
        },
        handleDropdownMouseEnter() {
            // Only cancel close timer on hover if not a touch device
            if (!this.isTouchDevice) {
                this.cancelCloseTimer();
            }
        },
        handleDropdownMouseLeave() {
            // Only start close timer on hover if not a touch device
            if (!this.isTouchDevice) {
                this.startCloseTimer();
            }
        },
        handleClickOutside(event) {
            if (this.$refs.languageSelector && !this.$refs.languageSelector.contains(event.target)) {
                this.closeDropdown();
            }
        },
        async selectLanguage(langCode) {
            this.selectedLanguage = langCode;
            this.closeDropdown();
            await this.setPreferenceLanguage(langCode);
        },
        buildLanguageUrl(langCode) {
            const url = new URL(window.location.href);
            url.searchParams.set('lang', langCode);
            return url.toString();
        },
        async setPreferenceLanguage(langCode) {
            this.isLoading = true;
            try {
                await axios.post('/api/v0/languages/set_preferred_language', {language: langCode}).then((response) => {
                    if (response.data.data === undefined) {
                        this.showAlertMessage(`Something went wrong while change the language. Please contact support`, 'error', 2000);
                    }

                    if (response.data.data.status === 'success') {
                        window.location.reload();
                    }
                }).catch(() => {
                    window.location.href = this.buildLanguageUrl(langCode);
                });
            } finally {
                this.isLoading = false;
            }
        },
    }
}
</script>

<style scoped>
.language-selector {
    position: relative;
    display: inline-block;
}

.language-toggle {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0.5rem 0.5rem;
    color: rgba(255, 255, 255, 0.75);
    text-decoration: none;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: color 0.15s ease-in-out;
    font-size: 0.9rem;
    line-height: 1.5;
}

.language-toggle:hover,
.language-toggle:focus {
    color: rgba(255, 255, 255, 1);
    text-decoration: none;
    outline: none;
}

.language-toggle.open {
    color: rgba(255, 255, 255, 1);
}

.language-toggle:disabled {
    color: rgba(255, 255, 255, 0.25);
    cursor: not-allowed;
}

.language-code {
    font-weight: 600;
    margin-top: -2px;
}


.language-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-4px);
    transition: all 0.2s ease;
}

.language-dropdown.open {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
}

.language-option {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 12px;
    background: transparent;
    border: none;
    color: #495057;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.15s ease;
}

.language-option:hover {
    background: #f8f9fa;
}

.language-option:focus {
    outline: none;
    background: #e3f2fd;
}

.language-option:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.language-option .language-code {
    font-weight: 600;
}

</style>



// WEBPACK FOOTER //
// src/Assets/components/Common/LanguageSwitcher.vue